import { useEffect, useState, useMemo } from "react";
import { UserPlus, Copy, RefreshCw, Trash2, Plus, ArrowDownUp, ShieldAlert } from "lucide-react";
import { api, formatApiError, ROLE_LABELS, STATUS_LABELS } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { isPlatformAuthEnabled } from "@/lib/flags";
import RoleBadge from "@/components/RoleBadge";
import { toast } from "sonner";

// Base roles selectable by an admin (manager is derived from workspace assignments)
const BASE_ROLES = ["admin", "member", "viewer"];
const ROLE_ORDER = { admin: 0, manager: 1, member: 2, viewer: 3 };
const SORT_KEY = "ruma_users_sort";

const STATUS_STYLES = {
  active: "bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-600/20",
  suspended: "bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-600/20",
  pending: "bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-600/20",
  invite_expired: "bg-red-50 text-red-700 ring-1 ring-inset ring-red-600/20",
};

function sortUsers(list, sortBy) {
  const arr = [...list];
  const byName = (a, b) => a.name.localeCompare(b.name, "pt-BR");
  switch (sortBy) {
    case "name_desc":
      arr.sort((a, b) => b.name.localeCompare(a.name, "pt-BR"));
      break;
    case "role":
      arr.sort(
        (a, b) =>
          (ROLE_ORDER[a.effective_role] ?? 9) - (ROLE_ORDER[b.effective_role] ?? 9) || byName(a, b)
      );
      break;
    case "status":
      arr.sort((a, b) => (a.status || "").localeCompare(b.status || "") || byName(a, b));
      break;
    case "date":
      arr.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
      break;
    case "name_asc":
    default:
      arr.sort(byName);
  }
  return arr;
}

// Read-only list of workspace chips
function WsChips({ items, testid, onRemove }) {
  if (!items || items.length === 0) return <span className="text-slate-400 text-xs">—</span>;
  return (
    <div className="flex flex-wrap gap-1" data-testid={testid}>
      {items.map((w) => (
        <span key={w.id} className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-700">
          {w.name}
          {onRemove && (
            <button onClick={() => onRemove(w.id)} title="Remover vínculo" className="hover:text-red-600" data-testid={`remove-ws-${w.id}`}>×</button>
          )}
        </span>
      ))}
    </div>
  );
}

export default function Users() {
  const { user } = useAuth();
  const [users, setUsers] = useState(null);
  const [workspaces, setWorkspaces] = useState([]);
  const [error, setError] = useState("");
  const [showInvite, setShowInvite] = useState(false);
  const [inviteForm, setInviteForm] = useState({ name: "", email: "", role: "member", workspace_id: "" });
  const [filter, setFilter] = useState(() => {
    const s = new URLSearchParams(window.location.search).get("status");
    return ["active", "pending", "suspended"].includes(s) ? s : "all";
  });
  const [sortBy, setSortBy] = useState(() => sessionStorage.getItem(SORT_KEY) || "name_asc");
  const [wsModal, setWsModal] = useState(null); // user being assigned to a departmental space
  const [wsSelected, setWsSelected] = useState("");
  const [demoteTarget, setDemoteTarget] = useState(null);
  const [demoting, setDemoting] = useState(false);
  const [linkBusy, setLinkBusy] = useState(null); // id do usuário sendo processado

  const isAdmin = user?.role === "admin";

  const load = async () => {
    try {
      const { data } = await api.get("/users");
      setUsers(data);
      if (isAdmin) {
        const { data: ws } = await api.get("/workspaces");
        setWorkspaces(ws);
      }
    } catch (e) { setError(formatApiError(e)); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { sessionStorage.setItem(SORT_KEY, sortBy); }, [sortBy]);

  const filtered = useMemo(() => {
    if (!users) return null;
    let list = users;
    if (filter === "pending") list = users.filter((u) => u.status === "pending" || u.status === "invite_expired");
    else if (filter !== "all") list = users.filter((u) => u.status === filter);
    return sortUsers(list, sortBy);
  }, [users, filter, sortBy]);

  const counts = useMemo(() => {
    if (!users) return {};
    return {
      all: users.length,
      active: users.filter((u) => u.status === "active").length,
      pending: users.filter((u) => u.status === "pending" || u.status === "invite_expired").length,
      suspended: users.filter((u) => u.status === "suspended").length,
    };
  }, [users]);

  const submitInvite = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...inviteForm };
      if (!payload.workspace_id) delete payload.workspace_id;
      await api.post("/users/invite", payload);
      toast.success("Convite gerado. Usuário aparece como pendente.");
      setShowInvite(false);
      setInviteForm({ name: "", email: "", role: "member", workspace_id: "" });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const resendInvite = async (uid) => {
    try { await api.post(`/users/${uid}/resend-invite`); toast.success("Novo link de convite gerado."); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const copyLink = async (uid) => {
    if (linkBusy) return;
    setLinkBusy(uid);
    try {
      // Modo legado: GET /users/{id}/invite-link.
      // Modo platform: POST /users/{id}/invite-link (interceptor
      // reescreve para /api/platform/... e cada geração rotaciona o
      // token, invalidando o anterior).
      const res = isPlatformAuthEnabled()
        ? await api.post(`/users/${uid}/invite-link`)
        : await api.get(`/users/${uid}/invite-link`);
      const url = res?.data?.accept_url;
      if (!url) {
        toast.error("Link indisponível.");
        return;
      }
      await navigator.clipboard.writeText(url);
      toast.success("Link de convite copiado. O link anterior foi invalidado.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLinkBusy(null);
    }
  };

  const cancelInvite = async (u) => {
    if (!window.confirm(`Cancelar convite de ${u.name}?`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success("Convite cancelado."); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const updateRole = async (uid, role) => {
    try { await api.patch(`/users/${uid}`, { role }); toast.success("Função atualizada."); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const confirmDemote = async () => {
    if (!demoteTarget) return;
    setDemoting(true);
    try {
      await api.post(`/users/${demoteTarget.id}/demote-manager`);
      toast.success(`${demoteTarget.name} agora é membro.`);
      setDemoteTarget(null);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setDemoting(false); }
  };

  const toggleStatus = async (u) => {
    const next = u.status === "active" ? "suspended" : "active";
    try { await api.patch(`/users/${u.id}`, { status: next }); toast.success(next === "active" ? "Reativado." : "Suspenso."); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const removeUser = async (u) => {
    if (!window.confirm(`Remover ${u.name}?`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success("Usuário removido."); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const assignWs = async (e) => {
    e.preventDefault();
    if (!wsSelected) return;
    try {
      // Envia ambas as chaves para manter compatibilidade cliente-side:
      //  - fluxo legado (``AddMemberIn``): usa ``user_id`` = workspace_oid (quirk histórico do handler ``@api.post("/users/{user_id}/workspaces")``, comentário “body.user_id here means workspace_id”);
      //  - fluxo global (``PlatformAssignWorkspaceIn``): usa ``workspace_id``.
      // Pydantic ignora a chave extra em cada schema; nenhum handler ou schema legado é modificado.
      await api.post(`/users/${wsModal.id}/workspaces`, { workspace_id: wsSelected, user_id: wsSelected });
      toast.success("Espaço vinculado.");
      setWsModal(null); setWsSelected(""); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const removeWs = async (uid, wsId) => {
    try { await api.delete(`/users/${uid}/workspaces/${wsId}`); toast.success("Espaço removido."); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Pessoas</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight mt-2">Usuários</h1>
          <p className="text-slate-500 mt-1">Função efetiva derivada das atribuições nos espaços.</p>
        </div>
        {isAdmin && (
          <button onClick={() => setShowInvite(true)} data-testid="users-invite-btn"
            className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-slate-900 text-white text-sm font-medium hover:bg-slate-800">
            <UserPlus className="w-4 h-4" /> Convidar usuário
          </button>
        )}
      </div>

      {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2" data-testid="users-filter">
          {[
            { v: "all", label: `Todos (${counts.all || 0})` },
            { v: "active", label: `Ativos (${counts.active || 0})` },
            { v: "pending", label: `Convite pendente (${counts.pending || 0})` },
            { v: "suspended", label: `Suspensos (${counts.suspended || 0})` },
          ].map((f) => (
            <button key={f.v} onClick={() => setFilter(f.v)} data-testid={`filter-${f.v}`}
              className={`h-9 px-3 rounded-lg text-sm border ${filter === f.v ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"}`}>
              {f.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <ArrowDownUp className="w-4 h-4 text-slate-400" />
          <label className="text-sm text-slate-500">Ordenar por</label>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} data-testid="users-sort-select"
            className="h-9 rounded-lg border border-slate-200 bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900">
            <option value="name_asc">Nome — A a Z</option>
            <option value="name_desc">Nome — Z a A</option>
            <option value="role">Função</option>
            <option value="status">Status</option>
            <option value="date">Data de criação</option>
          </select>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          {filtered === null ? (
            <div className="p-8 text-slate-500">Carregando...</div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center text-slate-500" data-testid="users-empty">Nenhum usuário neste filtro.</div>
          ) : (
            <table className="w-full text-sm" data-testid="users-table">
              <thead className="bg-slate-50/50 border-b border-slate-200">
                <tr>
                  {["Nome", "Função efetiva", "Gerencia", "Membro em", "Interdepartamental", "Status"].map((h) => (
                    <th key={h} className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">{h}</th>
                  ))}
                  {isAdmin && <th className="text-right px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Ações</th>}
                </tr>
              </thead>
              <tbody>
                {filtered.map((u) => {
                  const isManager = u.effective_role === "manager";
                  return (
                  <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50/50 align-top" data-testid={`user-row-${u.id}`}>
                    <td className="px-5 py-3">
                      <div className="text-slate-900 font-medium">{u.name}</div>
                      <div className="text-slate-500 text-xs">{u.email}</div>
                    </td>
                    <td className="px-5 py-3" data-testid={`effective-role-${u.id}`}>
                      {isAdmin && u.id !== user.id && !isManager ? (
                        <select value={u.effective_role} onChange={(e) => updateRole(u.id, e.target.value)} data-testid={`role-select-${u.id}`}
                          className="h-8 rounded-md border border-slate-200 bg-white px-2 text-xs">
                          {BASE_ROLES.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
                        </select>
                      ) : (
                        <div className="flex flex-col gap-1 items-start">
                          <RoleBadge role={u.effective_role} />
                          {isAdmin && u.id !== user.id && isManager && (
                            <button onClick={() => setDemoteTarget(u)} data-testid={`demote-btn-${u.id}`}
                              className="text-[11px] text-amber-700 hover:underline">Rebaixar para membro</button>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-5 py-3"><WsChips items={u.manages} testid={`manages-${u.id}`} /></td>
                    <td className="px-5 py-3" data-testid={`member-of-${u.id}`}>
                      <div className="flex flex-wrap gap-1 items-center">
                        <WsChips items={u.member_of} onRemove={isAdmin ? (wsId) => removeWs(u.id, wsId) : undefined} />
                        {isAdmin && (
                          <button onClick={() => { setWsModal(u); setWsSelected(""); }} data-testid={`add-ws-${u.id}`}
                            className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded border border-dashed border-slate-300 text-slate-600 hover:bg-slate-50">
                            <Plus className="w-3 h-3" /> Espaço
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="px-5 py-3"><WsChips items={u.interdepartmental} testid={`interdept-${u.id}`} /></td>
                    <td className="px-5 py-3">
                      <div className="space-y-1">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs ${STATUS_STYLES[u.status] || "bg-slate-100 text-slate-700"}`} data-testid={`status-${u.id}`}>
                          {STATUS_LABELS[u.status] || u.status}
                        </span>
                        {u.pending_invite && (
                          <div className="text-[11px] text-slate-500" data-testid={`invite-info-${u.id}`}>
                            Expira: {new Date(u.pending_invite.expires_at).toLocaleDateString("pt-BR")}
                          </div>
                        )}
                      </div>
                    </td>
                    {isAdmin && (
                      <td className="px-5 py-3 text-right">
                        <div className="inline-flex items-center gap-1 flex-wrap justify-end">
                          {u.pending_invite && (
                            <>
                              <button onClick={() => copyLink(u.id)} disabled={linkBusy === u.id}
                                title="Copiar link (invalida o anterior)" data-testid={`copy-invite-${u.id}`}
                                className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border border-slate-200 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed">
                                <Copy className="w-3 h-3" /> {linkBusy === u.id ? "Gerando..." : "Link"}
                              </button>
                              <button onClick={() => resendInvite(u.id)} title="Reenviar" data-testid={`resend-invite-${u.id}`}
                                className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border border-slate-200 hover:bg-slate-50">
                                <RefreshCw className="w-3 h-3" /> Reenviar
                              </button>
                              <button onClick={() => cancelInvite(u)} title="Cancelar convite" data-testid={`cancel-invite-${u.id}`}
                                className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border border-red-200 text-red-700 hover:bg-red-50">
                                <Trash2 className="w-3 h-3" /> Cancelar
                              </button>
                            </>
                          )}
                          {!u.pending_invite && u.id !== user.id && (
                            <>
                              <button onClick={() => toggleStatus(u)} className="text-xs px-2 py-1 rounded border border-slate-200 hover:bg-slate-50" data-testid={`toggle-status-${u.id}`}>
                                {u.status === "active" ? "Suspender" : "Reativar"}
                              </button>
                              <button onClick={() => removeUser(u)} className="text-xs px-2 py-1 rounded border border-red-200 text-red-700 hover:bg-red-50" data-testid={`delete-user-${u.id}`}>
                                Remover
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showInvite && (
        <div className="fixed inset-0 z-50 bg-slate-900/30 grid place-items-center p-4" onClick={() => setShowInvite(false)} data-testid="invite-modal">
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl border border-slate-200 shadow-lg w-full max-w-md p-6 space-y-4">
            <h2 className="font-heading text-lg font-semibold">Convidar usuário</h2>
            <form onSubmit={submitInvite} className="space-y-4" data-testid="invite-form">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Nome</label>
                <input required value={inviteForm.name} onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
                  data-testid="invite-name-input"
                  className="w-full h-10 rounded-lg border border-slate-200 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">E-mail</label>
                <input type="email" required value={inviteForm.email} onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                  data-testid="invite-email-input"
                  className="w-full h-10 rounded-lg border border-slate-200 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Função</label>
                <select value={inviteForm.role} onChange={(e) => setInviteForm({ ...inviteForm, role: e.target.value })}
                  data-testid="invite-role-select"
                  className="w-full h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900">
                  {BASE_ROLES.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
                </select>
                <p className="text-xs text-slate-500 mt-1">Gerente é atribuído pela página do espaço.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Espaço (opcional)</label>
                <select value={inviteForm.workspace_id} onChange={(e) => setInviteForm({ ...inviteForm, workspace_id: e.target.value })}
                  data-testid="invite-workspace-select"
                  className="w-full h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900">
                  <option value="">Nenhum</option>
                  {workspaces.filter((w) => w.type !== "interdepartmental").map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
                </select>
              </div>
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setShowInvite(false)} className="h-10 px-4 rounded-lg border border-slate-200 text-sm">Cancelar</button>
                <button type="submit" data-testid="invite-submit-button"
                  className="h-10 px-4 rounded-lg bg-slate-900 text-white text-sm font-medium hover:bg-slate-800">Gerar convite</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {wsModal && (
        <div className="fixed inset-0 z-50 bg-slate-900/30 grid place-items-center p-4" onClick={() => setWsModal(null)} data-testid="assign-ws-modal">
          <form onClick={(e) => e.stopPropagation()} onSubmit={assignWs}
            className="bg-white rounded-xl border border-slate-200 shadow-lg w-full max-w-md p-6 space-y-4">
            <h2 className="font-heading text-lg font-semibold">Vincular {wsModal.name} a um espaço departamental</h2>
            <p className="text-xs text-slate-500">Participação em espaços interdepartamentais é administrada na página do próprio espaço.</p>
            <select required value={wsSelected} onChange={(e) => setWsSelected(e.target.value)} data-testid="assign-ws-select"
              className="w-full h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900">
              <option value="">Selecione...</option>
              {workspaces
                .filter((w) => w.type !== "interdepartmental"
                  && !(wsModal.member_of || []).some((ww) => ww.id === w.id)
                  && !(wsModal.manages || []).some((ww) => ww.id === w.id))
                .map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
            </select>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setWsModal(null)} className="h-10 px-4 rounded-lg border border-slate-200 text-sm">Cancelar</button>
              <button type="submit" disabled={!wsSelected} data-testid="assign-ws-submit"
                className="h-10 px-4 rounded-lg bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 disabled:opacity-50">Vincular</button>
            </div>
          </form>
        </div>
      )}

      {demoteTarget && (
        <div className="fixed inset-0 z-50 bg-slate-900/30 grid place-items-center p-4" onClick={() => !demoting && setDemoteTarget(null)} data-testid="demote-modal">
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl border border-slate-200 shadow-lg w-full max-w-md p-6 space-y-4">
            <div className="flex items-center gap-2 text-amber-700">
              <ShieldAlert className="w-5 h-5" />
              <h2 className="font-heading text-lg font-semibold text-slate-900">Rebaixar {demoteTarget.name} para Membro</h2>
            </div>
            <div className="space-y-3 text-sm">
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Departamentos que ficarão sem gerente</div>
                {demoteTarget.manages?.length ? (
                  <ul className="list-disc list-inside text-slate-700" data-testid="demote-vacated">
                    {demoteTarget.manages.map((w) => <li key={w.id}>{w.name}</li>)}
                  </ul>
                ) : <span className="text-slate-400">Nenhum</span>}
              </div>
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Sairá dos espaços interdepartamentais</div>
                {demoteTarget.interdepartmental?.length ? (
                  <ul className="list-disc list-inside text-slate-700" data-testid="demote-left-inter">
                    {demoteTarget.interdepartmental.map((w) => <li key={w.id}>{w.name}</li>)}
                  </ul>
                ) : <span className="text-slate-400">Nenhum</span>}
              </div>
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Permanecerá como membro em</div>
                {(demoteTarget.manages?.length || demoteTarget.member_of?.length) ? (
                  <ul className="list-disc list-inside text-slate-700" data-testid="demote-keeps-member">
                    {[...(demoteTarget.manages || []), ...(demoteTarget.member_of || [])].map((w) => <li key={w.id}>{w.name}</li>)}
                  </ul>
                ) : <span className="text-slate-400">Nenhum</span>}
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setDemoteTarget(null)} disabled={demoting}
                className="h-10 px-4 rounded-lg border border-slate-200 text-sm" data-testid="demote-cancel">Cancelar</button>
              <button type="button" onClick={confirmDemote} disabled={demoting} data-testid="demote-confirm"
                className="h-10 px-4 rounded-lg bg-amber-600 text-white text-sm font-medium hover:bg-amber-700 disabled:opacity-50">
                {demoting ? "Processando..." : "Confirmar rebaixamento"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
